import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import type { ChapterReportRecommendationCandidate } from '@/types/chapterReport'

interface Props {
  candidates: ChapterReportRecommendationCandidate[]
  selectedLessonIds: number[]
  editable?: boolean
  onChange?: (lessonIds: number[]) => void
}

export function ReviewRecommendationEditor({
  candidates,
  selectedLessonIds,
  editable = false,
  onChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const selected = new Set(selectedLessonIds)
  const visible = editable
    ? candidates
    : candidates.filter((candidate) => selected.has(candidate.lessonId))

  const toggle = (lessonId: number) => {
    const next = new Set(selected)
    if (next.has(lessonId)) next.delete(lessonId)
    else next.add(lessonId)
    onChange?.(candidates.filter((candidate) => next.has(candidate.lessonId)).map((candidate) => candidate.lessonId))
  }

  return (
    <div
      data-testid="chapter-report-recommendations"
      className="rounded-xl bg-[#edf5ff] px-5 py-4"
    >
      <h3 className="text-sm font-bold tracking-[-0.6px] text-[#4599ff]">
        {t('chapterReport.recommendations')}
      </h3>
      <p className="mt-2 text-xs leading-[1.5] text-[#131313]">
        {editable ? (
          <>
            {t('chapterReport.recommendationsEditHelp')}
            <span className="mt-1 block text-[#ff675e]">
              {t('chapterReport.recommendationsEditWarning')}
            </span>
          </>
        ) : t('chapterReport.recommendationsHelp')}
      </p>
      {visible.length === 0 ? (
        <p className="mt-4 rounded-lg bg-white px-3 py-2 text-xs text-[#888]">
          {t('chapterReport.recommendationEmpty')}
        </p>
      ) : (
        <ul className="mt-4 space-y-2">
          {visible.map((candidate) => (
            <li key={candidate.lessonId}>
              <label className="flex min-h-9 cursor-pointer items-center justify-between gap-3 rounded-lg bg-white px-2.5 py-1.5 text-xs">
                <span className="flex min-w-0 items-center gap-2 text-[#333]">
                  {editable && (
                    <input
                      type="checkbox"
                      checked={selected.has(candidate.lessonId)}
                      onChange={() => toggle(candidate.lessonId)}
                      aria-label={t('chapterReport.recommendationAria', {
                        number: candidate.lessonNumber,
                        title: candidate.title,
                      })}
                      className="size-4 accent-[#4599ff]"
                    />
                  )}
                  <strong>{candidate.lessonNumber}.</strong>
                  <span><ContentMetadataText text={candidate.title} /></span>
                </span>
                {!editable && (
                  <span className="flex shrink-0 items-center gap-4">
                    <img src="/report/diagnostic/icon-O.svg" alt="" className="size-5" />
                    <span className="rounded-full bg-[#ff675e] px-2 py-1 font-semibold text-white">
                      {t('chapterReport.review')}
                    </span>
                  </span>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
